import React from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import Dropdown from '../../../components/Dropdown/Dropdown'
import { FILTER_CONTROL } from './filterControlClass'
import { useThemeContext } from '../../../theme/ThemeContext'
import { formatMoney } from '../../../utils/amount'
import { formatBusinessDate } from '../../../utils/date'
import { Arrow } from '../../../components/Icons/Icons'
import { LuLineChart, LuAlertTriangle, LuRotateCw } from 'react-icons/lu'

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

export const METRIC_OPTIONS = [
  { value: 'newEnrollments', label: 'New Enrollments' },
  { value: 'clearedPayments', label: 'Cleared Payments' },
  { value: 'totalPayments', label: 'Total Payments' },
  { value: 'totalCanceled', label: 'Canceled Client' },
  { value: 'returnedPayments', label: 'Returned Payments' },
  // 'pendingPayments' is deliberately absent. It counted `scheduled` (never submitted) together
  // with `processing` (submitted, awaiting settlement) and filtered both by dueDate — a forecast
  // and a receivable in one figure, which moved whenever the period toggle did. 'inTransitPayments'
  // replaced it as the card; this keeps the same number from being charted after the card was
  // retired. The API still returns the key: the quantity behind it is the clearedRatio
  // denominator's third term, so it must keep counting `processing` server-side.
  { value: 'inTransitPayments', label: 'In Transit' },
]

export const MONEY_METRICS = ['clearedPayments', 'totalPayments', 'returnedPayments', 'inTransitPayments']

export const DRILLABLE_METRICS = ['newEnrollments', 'totalCanceled', 'clearedPayments', 'returnedPayments', 'inTransitPayments']

// Takes no period: a live snapshot of money a gateway accepted and has not settled. The card,
// the chart and the list all ignore the date filter for it, and say so on the card's face.
export const SNAPSHOT_METRICS = ['inTransitPayments']

const VIEWS = [
  { value: 'chart', label: 'Chart' },
  { value: 'list', label: 'List' },
]

export const dayAtIndex = (data, index) =>
  (index === null || index === undefined || index < 0 ? null : data?.[index]?.date ?? null)

const MetricsChart = ({
  data = [],
  metricType,
  onMetricChange,
  percentChange,
  comparisonLabel,
  paymentCount = 0,
  menuItems = METRIC_OPTIONS,
  error = null,
  onRetry,
  view = 'chart',
  onViewChange,
  onDayClick,
  listSlot = null,
}) => {
  const { mode } = useThemeContext()

  const isMoney = MONEY_METRICS.includes(metricType)
  const isDrillable = DRILLABLE_METRICS.includes(metricType)
  // Resolved against the full METRIC_OPTIONS first: the caller may hand us a narrowed
  // menu (money metrics stripped for reps).
  const selectedLabel = (METRIC_OPTIONS.find((o) => o.value === metricType) || menuItems.find((o) => o.value === metricType) || {}).label || 'Details'

  // Calculate average from chart data
  // For money metrics: total amount / payment count (average per payment)
  // For other metrics: sum / days (daily average)
  const calculateAverage = () => {
    if (data.length === 0) return 0
    const sum = data.reduce((acc, item) => acc + item.value, 0)

    // For money metrics, calculate average per payment
    if (isMoney && paymentCount > 0) {
      return sum / paymentCount
    }

    // For other metrics, calculate daily average
    return sum / data.length
  }

  const averageValue = calculateAverage()
  const showAverage = data.length > 0
  const showPercentChange = percentChange !== null && percentChange !== undefined && !isNaN(percentChange)

  // Dynamic label based on metric type
  const getAverageLabel = () => {
    if (isMoney) return 'Average payment amount'
    if (metricType === 'newEnrollments') return 'Daily average enrollments'
    if (metricType === 'totalCanceled') return 'Daily average canceled'
    if (metricType === 'creditPulls') return 'Daily average pulls'
    return 'Daily average'
  }

  // Format average value based on metric type
  const formatAverageValue = () => {
    if (isMoney) return formatMoney(averageValue)
    return averageValue.toFixed(1)
  }

  // Arrow colors for percent change
  const getArrowColors = () => {
    if (!showPercentChange || percentChange === 0) {
      return {
        fill: 'var(--color-text-secondary)',
        fillBg: 'var(--color-gray-200)',
      }
    }
    // For canceled metric, increase is bad
    const invertColors = metricType === 'totalCanceled'
    const isGood = invertColors ? percentChange < 0 : percentChange > 0

    if (isGood) {
      return {
        fill: 'var(--color-success-600)',
        fillBg: 'rgba(18,183,106,0.16)',
      }
    }
    return {
      fill: 'var(--color-error-600)',
      fillBg: 'rgba(240,68,56,0.16)',
    }
  }

  const getTextColorClass = () => {
    if (!showPercentChange || percentChange === 0) return 'text-text-secondary'
    const invertColors = metricType === 'totalCanceled'
    const isGood = invertColors ? percentChange < 0 : percentChange > 0
    return isGood ? 'text-success-600' : 'text-error-600'
  }

  const formatPercentChange = () => {
    const sign = percentChange > 0 ? '+' : ''
    return `${sign}${percentChange.toFixed(1)}%`
  }

  const arrowColors = getArrowColors()

  // Кольори на основі теми (рефайн: мʼякі осі, ледь помітна сітка)
  const tickColor = mode === 'dark' ? '#94A0B8' : '#667085'
  const gridColor = mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#EAECF0'
  const seriesColor = '#5E6AD2' // brand indigo (not generic bright blue)

  // The bucket key is a business-timezone calendar day ('2026-08-05'). `new Date(key)` reads it
  // as UTC midnight, so local getters name the previous day for any viewer west of UTC — and the
  // tick is now clickable, so the operator would audit a day other than the one they clicked.
  const labels = data.map(item => formatBusinessDate(item.date, 'MM/DD'))

  const values = data.map(item => item.value)
  const maxValue = Math.max(...values, isMoney ? 100 : 10)

  const chartData = {
    labels,
    datasets: [{
      data: values,
      backgroundColor: (context) => {
        const bgColor = [
          'rgba(94,106,210,0.5)',
          'rgba(94,106,210,0.2)',
          'rgba(94,106,210,0.16)',
          'rgba(94,106,210,0.05)',
          'rgba(94,106,210,0.01)',
        ]
        if (!context.chart.chartArea || !context.chart.ctx) {
          return
        }
        const { ctx, chartArea: { top, bottom } } = context.chart
        const gradientBg = ctx.createLinearGradient(0, top, 0, bottom)
        gradientBg.addColorStop(0, bgColor[0])
        gradientBg.addColorStop(0.1, bgColor[1])
        gradientBg.addColorStop(0.2, bgColor[2])
        gradientBg.addColorStop(0.8, bgColor[3])
        gradientBg.addColorStop(1, bgColor[4])
        return gradientBg
      },
      borderColor: seriesColor,
      borderWidth: 2,
      pointBackgroundColor: seriesColor,
      pointBorderColor: mode === 'dark' ? '#141B2E' : '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      clip: false, // don't clip points at the chart-area edge (fixes cut-off zero points)
      fill: {
        target: 'origin',
      },
      tension: 0.3,
    }]
  }

  const formatValue = (value) => {
    if (isMoney) {
      return `$${value.toLocaleString()}`
    }
    return value.toLocaleString()
  }

  const handleChartClick = (_event, elements) => {
    if (!onDayClick) return
    const day = dayAtIndex(data, elements?.[0]?.index)
    if (day) onDayClick(day)
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Only a drillable series may open a day. Wiring the click for the others opened a List
    // view they have no columns for — and the List|Chart toggle is hidden for exactly those
    // metrics, so there was no way back to the chart.
    onClick: onDayClick && isDrillable ? handleChartClick : undefined,
    interaction: onDayClick && isDrillable ? { mode: 'index', intersect: false } : undefined,
    layout: {
      padding: { top: 12, bottom: 12, left: 4, right: 10 },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 14 },
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => formatValue(context.raw)
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          padding: 10,
          color: tickColor,
        },
        border: {
          color: 'transparent',
        }
      },
      y: {
        min: 0,
        max: isMoney
          ? Math.ceil(maxValue * 1.1 / 100) * 100
          : Math.ceil(maxValue * 1.1),
        ticks: {
          padding: 10,
          color: tickColor,
          callback: (value) => formatValue(value)
        },
        border: {
          dash: [2],
          color: 'transparent',
        },
        grid: {
          color: gridColor,
        }
      }
    }
  }

  return (
    <div className="border border-divider rounded-2xl bg-surface shadow-subtle dark:shadow-none overflow-hidden">
      {/* Header - Details + Dropdown */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-5 border-b border-divider">
        <div>
          <h3 className="font-semibold text-[16px] text-text-primary">{selectedLabel} Overview</h3>
          <p className="text-[13px] text-text-secondary mt-0.5">
            See breakdown by day within the selected period
          </p>
        </div>
        <div className="flex items-center gap-3">
          {onViewChange && isDrillable && (
            <div className="inline-flex rounded-lg border border-divider overflow-hidden" role="group" aria-label="Details view">
              {VIEWS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={view === value}
                  onClick={() => onViewChange(value)}
                  className={`px-3 h-9 text-[13px] font-medium transition-colors ${
                    view === value
                      ? 'bg-primary-main text-white'
                      : 'bg-surface text-text-secondary hover:bg-divider'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <Dropdown
            menuItems={menuItems}
            value={metricType}
            placeholder="Select metric"
            setValue={onMetricChange}
            width="w-[200px]"
            ariaLabel="Chart metric"
            className={`w-[200px] ${FILTER_CONTROL}`}
          />
        </div>
      </div>

      {view === 'list' && isDrillable && listSlot ? (
        <div className="p-5">{listSlot}</div>
      ) : (
      /* Content - Average + Chart */
      <div className="flex flex-col lg:flex-row">
        {/* Left section - Average */}
        {showAverage && (
          <div className="lg:w-1/4 min-w-[200px] p-5 lg:border-r border-b lg:border-b-0 border-divider">
            <p className="font-semibold text-[14px] text-text-secondary">
              {getAverageLabel()}
            </p>
            <p className="font-bold text-[32px] text-text-primary pt-3">
              {formatAverageValue()}
            </p>
            {showPercentChange && (
              <div className="flex items-center gap-1.5 pt-2">
                <Arrow
                  isArrowUp={percentChange > 0}
                  fill={arrowColors.fill}
                  fillBg={arrowColors.fillBg}
                />
                <span className={`text-[14px] font-medium ${getTextColorClass()}`}>
                  {formatPercentChange()}
                </span>
                {comparisonLabel && (
                  <span className="text-[13px] text-text-secondary ml-1">
                    {comparisonLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Right section - Chart */}
        <div className={`${showAverage ? 'lg:w-3/4' : 'w-full'} p-5`}>
          {error ? (
            <div className="h-[280px] flex flex-col items-center justify-center text-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(240,68,56,0.12)' }}>
                <LuAlertTriangle size={20} style={{ color: 'var(--color-error-main)' /* non-text: 20px icon, answers to 3:1 */ }} />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-text-primary">Couldn’t load chart data</p>
                <p className="text-[13px] text-text-secondary mt-1">Something went wrong while fetching the breakdown.</p>
              </div>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-2 rounded-lg border border-divider bg-surface px-4 h-9 text-[13px] font-medium text-text-primary hover:bg-divider transition-colors"
                >
                  <LuRotateCw size={16} /> Retry
                </button>
              )}
            </div>
          ) : data.length === 0 ? (
            <div className="h-[280px] flex flex-col items-center justify-center text-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(94,106,210,0.12)' }}>
                <LuLineChart size={20} style={{ color: 'var(--color-primary-main)' /* non-text: 20px icon, answers to 3:1 */ }} />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-text-primary">No data for the selected period</p>
                <p className="text-[13px] text-text-secondary mt-1">Try changing the date range or removing some filters.</p>
              </div>
            </div>
          ) : (
            <div className="h-[280px]">
              <Line key={mode} data={chartData} options={options} />
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  )
}

export default MetricsChart
